import { useState } from 'react';
import { ArrowLeft, Video, Calendar, User, X, CheckCircle } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { telehealthService } from '../services/telehealth.service';
import { LoadingSkeleton } from './LoadingSkeleton';
import toast from 'react-hot-toast';
import { handleApiError } from '../lib/api-client';

interface TeleHealthConsultProps {
  onBack: () => void;
}

export function TeleHealthConsult({ onBack }: TeleHealthConsultProps) {
  const { user } = useAuth();
  const queryClient = useQueryClient();
  const [showBookingModal, setShowBookingModal] = useState(false);
  const [selectedDoctor, setSelectedDoctor] = useState<any>(null);
  const [bookingDate, setBookingDate] = useState('');
  const [bookingTime, setBookingTime] = useState('');
  const [reason, setReason] = useState('');

  // Fetch doctors
  const {
    data: doctors = [],
    isLoading: doctorsLoading,
    error: doctorsError,
  } = useQuery({
    queryKey: ['doctors'],
    queryFn: () => telehealthService.getDoctors({ limit: 50 }),
  });

  // Fetch appointments
  const {
    data: appointments = [],
    isLoading: appointmentsLoading,
    error: appointmentsError,
  } = useQuery({
    queryKey: ['appointments', user?.id],
    queryFn: () => telehealthService.getAppointments(),
    enabled: !!user,
  });

  // Book appointment mutation
  const bookMutation = useMutation({
    mutationFn: (data: { doctorId: string; scheduledAt: string; duration?: number; reason?: string }) =>
      telehealthService.bookAppointment(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['appointments'] });
      toast.success('Appointment booked successfully!');
      setShowBookingModal(false);
      setSelectedDoctor(null);
      setBookingDate('');
      setBookingTime('');
      setReason('');
    },
    onError: (error) => {
      toast.error(handleApiError(error));
    },
  });

  const handleBookAppointment = () => {
    if (!selectedDoctor || !bookingDate || !bookingTime) {
      toast.error('Please fill in all required fields');
      return;
    }

    const scheduledAt = new Date(`${bookingDate}T${bookingTime}`);
    bookMutation.mutate({
      doctorId: selectedDoctor.id,
      scheduledAt: scheduledAt.toISOString(),
      duration: 30,
      reason: reason || undefined,
    });
  };

  const openBookingModal = (doctor: any) => {
    setSelectedDoctor(doctor);
    setShowBookingModal(true);
    // Set default date/time (next available slot)
    const tomorrow = new Date();
    tomorrow.setDate(tomorrow.getDate() + 1);
    setBookingDate(tomorrow.toISOString().split('T')[0]);
    setBookingTime('09:00');
  };

  if (doctorsError) {
    toast.error(handleApiError(doctorsError));
  }
  if (appointmentsError) {
    toast.error(handleApiError(appointmentsError));
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-blue-50">
      <div className="bg-white border-b border-gray-200 shadow-sm sticky top-0 z-40">
        <div className="max-w-7xl mx-auto px-6 py-4">
          <div className="flex items-center justify-between">
            <button
              onClick={onBack}
              className="flex items-center space-x-2 text-gray-700 hover:text-dropsugar-green transition-colors"
              aria-label="Back to Dashboard"
            >
              <ArrowLeft className="w-5 h-5" />
              <span className="font-medium">Back to Dashboard</span>
            </button>
            <h1 className="text-xl font-bold text-gray-900">TeleHealth Consult</h1>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-6 py-8">
        <div className="bg-gradient-to-br from-teal-500 to-teal-600 rounded-2xl p-8 mb-8 text-white">
          <div className="flex items-start space-x-4">
            <div className="p-3 bg-white/20 backdrop-blur-md rounded-xl">
              <Video className="w-8 h-8" />
            </div>
            <div>
              <h2 className="text-2xl font-bold mb-2">Book a Virtual Consultation</h2>
              <p className="text-teal-100">
                Connect with verified healthcare specialists from the comfort of your home
              </p>
            </div>
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          <div className="lg:col-span-2">
            <h3 className="text-2xl font-bold text-gray-900 mb-6">Available Doctors</h3>
            {doctorsLoading ? (
              <div className="space-y-4">
                {[1, 2, 3].map((i) => (
                  <LoadingSkeleton key={i} variant="rectangular" height="150px" />
                ))}
              </div>
            ) : doctors.length === 0 ? (
              <div className="bg-white rounded-2xl p-12 border-2 border-gray-200 text-center">
                <User className="w-16 h-16 text-gray-400 mx-auto mb-4" />
                <h3 className="text-2xl font-bold text-gray-900 mb-2">No Doctors Available</h3>
                <p className="text-gray-600">Please check back later</p>
              </div>
            ) : (
              <div className="space-y-4">
                {doctors
                  .filter((doc: any) => doc.isVerified && doc.isApproved)
                  .map((doctor: any) => (
                    <div
                      key={doctor.id}
                      className="bg-white rounded-2xl p-6 border-2 border-gray-200 shadow-sm hover:shadow-md transition-all"
                    >
                      <div className="flex items-start justify-between">
                        <div className="flex-1">
                          <div className="flex items-center space-x-3 mb-2">
                            <h4 className="text-xl font-bold text-gray-900">
                              Dr. {doctor.user?.profile?.displayName || 'Doctor'}
                            </h4>
                            {doctor.isVerified && (
                              <CheckCircle className="w-5 h-5 text-green-600" aria-label="Verified" />
                            )}
                          </div>
                          <p className="text-dropsugar-green font-semibold mb-2">{doctor.specialization}</p>
                          <p className="text-gray-600 text-sm mb-2">
                            {doctor.qualifications?.join(', ') || 'Medical Professional'}
                          </p>
                          <p className="text-gray-600 text-sm mb-4">
                            {doctor.experience} years of experience
                          </p>
                          <div className="flex items-center space-x-4">
                            <div className="flex items-center space-x-2">
                              <Calendar className="w-4 h-4 text-gray-500" />
                              <span className="text-gray-700 font-semibold">
                                ₹{Number(doctor.consultationFee).toLocaleString('en-IN')}/consultation
                              </span>
                            </div>
                            {doctor.rating && (
                              <div className="flex items-center space-x-1">
                                <span className="text-yellow-500">★</span>
                                <span className="text-gray-700">{doctor.rating.toFixed(1)}</span>
                              </div>
                            )}
                          </div>
                        </div>
                        <button
                          onClick={() => openBookingModal(doctor)}
                          className="px-6 py-3 bg-teal-600 hover:bg-teal-700 text-white font-semibold rounded-xl transition-all shadow-md hover:shadow-lg"
                        >
                          Book Appointment
                        </button>
                      </div>
                    </div>
                  ))}
              </div>
            )}
          </div>

          <div className="lg:col-span-1">
            <h3 className="text-2xl font-bold text-gray-900 mb-6">My Appointments</h3>
            {appointmentsLoading ? (
              <LoadingSkeleton variant="rectangular" height="300px" />
            ) : appointments.length === 0 ? (
              <div className="bg-white rounded-2xl p-8 border-2 border-gray-200 text-center">
                <Calendar className="w-12 h-12 text-gray-400 mx-auto mb-4" />
                <p className="text-gray-600">No appointments scheduled</p>
              </div>
            ) : (
              <div className="space-y-4">
                {appointments.map((appointment: any) => (
                  <div
                    key={appointment.id}
                    className="bg-white rounded-2xl p-6 border-2 border-gray-200 shadow-sm"
                  >
                    <div className="flex items-start justify-between mb-4">
                      <div>
                        <p className="font-semibold text-gray-900">
                          {appointment.doctor?.specialization || 'Consultation'}
                        </p>
                        <p className="text-sm text-gray-600">
                          {new Date(appointment.scheduledAt).toLocaleDateString('en-US', {
                            month: 'short',
                            day: 'numeric',
                            year: 'numeric',
                          })}
                        </p>
                        <p className="text-sm text-gray-600">
                          {new Date(appointment.scheduledAt).toLocaleTimeString('en-US', {
                            hour: '2-digit',
                            minute: '2-digit',
                          })}
                        </p>
                      </div>
                      <span
                        className={`px-3 py-1 rounded-full text-xs font-semibold ${
                          appointment.status === 'CONFIRMED'
                            ? 'bg-green-100 text-green-800'
                            : appointment.status === 'COMPLETED'
                              ? 'bg-dropsugar-green/20 text-dropsugar-blue-dark'
                              : appointment.status === 'CANCELLED'
                                ? 'bg-red-100 text-red-800'
                                : 'bg-yellow-100 text-yellow-800'
                        }`}
                      >
                        {appointment.status}
                      </span>
                    </div>
                    {appointment.reason && (
                      <p className="text-sm text-gray-600 mt-2">{appointment.reason}</p>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>

      {showBookingModal && selectedDoctor && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center p-6 z-50">
          <div className="bg-white rounded-2xl p-8 max-w-md w-full shadow-xl">
            <div className="flex items-center justify-between mb-6">
              <h3 className="text-2xl font-bold text-gray-900">Book Appointment</h3>
              <button
                onClick={() => setShowBookingModal(false)}
                className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
                aria-label="Close"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Doctor</label>
                <p className="text-gray-900 font-semibold">
                  Dr. {selectedDoctor.user?.profile?.displayName || 'Doctor'} -{' '}
                  {selectedDoctor.specialization}
                </p>
              </div>

              <div>
                <label htmlFor="booking-date" className="block text-sm font-medium text-gray-700 mb-2">
                  Date
                </label>
                <input
                  type="date"
                  id="booking-date"
                  value={bookingDate}
                  onChange={(e) => setBookingDate(e.target.value)}
                  min={new Date().toISOString().split('T')[0]}
                  className="w-full px-4 py-3 border-2 border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-teal-500 focus:border-transparent text-gray-900"
                  required
                />
              </div>

              <div>
                <label htmlFor="booking-time" className="block text-sm font-medium text-gray-700 mb-2">
                  Time
                </label>
                <input
                  type="time"
                  id="booking-time"
                  value={bookingTime}
                  onChange={(e) => setBookingTime(e.target.value)}
                  className="w-full px-4 py-3 border-2 border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-teal-500 focus:border-transparent text-gray-900"
                  required
                />
              </div>

              <div>
                <label htmlFor="reason" className="block text-sm font-medium text-gray-700 mb-2">
                  Reason (Optional)
                </label>
                <textarea
                  id="reason"
                  value={reason}
                  onChange={(e) => setReason(e.target.value)}
                  rows={3}
                  className="w-full px-4 py-3 border-2 border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-teal-500 focus:border-transparent resize-none text-gray-900"
                  placeholder="Brief description of your concern..."
                />
              </div>

              <div className="flex space-x-3 pt-4">
                <button
                  onClick={() => setShowBookingModal(false)}
                  className="flex-1 px-6 py-3 bg-gray-200 hover:bg-gray-300 text-gray-900 font-semibold rounded-xl transition-all"
                >
                  Cancel
                </button>
                <button
                  onClick={handleBookAppointment}
                  disabled={bookMutation.isPending}
                  className="flex-1 px-6 py-3 bg-teal-600 hover:bg-teal-700 disabled:bg-gray-400 text-white font-semibold rounded-xl transition-all disabled:cursor-not-allowed"
                >
                  {bookMutation.isPending ? 'Booking...' : 'Book'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
